import { Box, type SxProps, type Theme, useTheme } from "@mui/material";
import type { PropsWithChildren } from "react";
import { forwardRef, useCallback, useId, useRef } from "react";
import {
	hideNativeScrollbarSx,
	useOverlayScrollbar,
} from "web_myplann/Shared/Hooks/useOverlayScrollbar";
import { SabCircularProgress } from "web_myplann/Shared/Progresses";

export interface MobileLayoutContentProps {
	disableContentPadding?: boolean;
	disableBottomPadding?: boolean;
	refreshing?: boolean;
	mainSx?: SxProps<Theme>;
	handlers?: any;
}

export const MobileLayoutContent = forwardRef<
	HTMLDivElement,
	PropsWithChildren<MobileLayoutContentProps>
>((props, ref) => {
	const { children, disableContentPadding, refreshing, mainSx, handlers } =
		props;
	const theme = useTheme();
	const id = useId();
	const scrollerRef = useRef<HTMLDivElement | null>(null);

	const handleRef = useCallback(
		(node: HTMLDivElement | null) => {
			scrollerRef.current = node;
			if (typeof ref === "function") ref(node);
			else if (ref) ref.current = node;
		},
		[ref],
	);

	useOverlayScrollbar(scrollerRef);

	if (!children) return null;

	return (
		<Box
			id={id}
			flexGrow={1}
			paddingX={disableContentPadding ? undefined : 1.6}
			component="main"
			sx={[
				{
					overflow: "auto",
					overscrollBehavior: "none",
					minHeight: 0,
					paddingBottom: 0,
					...hideNativeScrollbarSx,
				},
				...(Array.isArray(mainSx) ? mainSx : mainSx ? [mainSx] : []),
			]}
			ref={handleRef}
			{...handlers}
		>
			{refreshing && (
				<Box
					left="50%"
					top="50%"
					position="fixed"
					zIndex={theme.zIndex.tooltip}
					sx={{ transform: "translate(-50%, -50%)" }}
				>
					<SabCircularProgress size={"xxl"} />
				</Box>
			)}

			{children}
		</Box>
	);
});
